import React from 'react'
import Select from 'react-select'
import { useGetAllBusinesses, useGetBusinessIDIfExists } from '@marketing-milk/frontend'
import { useSwitchBusiness } from '../../useSwitchBusiness'

interface Props {
  expanded: boolean
  /** Collapsed, the control is an icon; clicking it opens the rail to reveal the real one. */
  onRequestExpand: () => void
}

type Option = { value: number; label: string }

// The control matches the new dashboard's switcher: a purple ground and border so
// it reads as a control rather than more rail. The open list is light, because a
// navy list on the navy rail gives no edge to show where the switcher ends.
const railSelectStyles = {
  // The switcher sits above the section list, so anything it contributes to
  // the block's height shunts every link below it the moment a peek opens.
  // The rail fixes that height; these make the control fill it exactly rather
  // than assert a second one that has to be kept in agreement.
  container: (base: any) => ({ ...base, width: '100%' }),
  control: (base: any) => ({
    ...base,
    minHeight: 0,
    height: '100%',
    backgroundColor: '#3A0269',
    borderColor: '#A044FF',
    boxShadow: 'none',
    ':hover': { borderColor: '#BD7EFF' },
  }),
  // Dimmed while the menu is open, so the current name reads as a hint that
  // typing replaces rather than text that has to be cleared first.
  singleValue: (base: any, state: any) => ({
    ...base,
    color: state.selectProps.menuIsOpen ? '#D6B0FF' : '#FFFFFF',
    fontSize: 13,
  }),
  placeholder: (base: any) => ({ ...base, color: '#D6B0FF', fontSize: 13 }),
  input: (base: any) => ({ ...base, color: '#FFFFFF' }),
  menu: (base: any) => ({
    ...base,
    backgroundColor: '#FFFFFF',
    border: '1px solid #A044FF',
    zIndex: 60,
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: 13,
    backgroundColor: state.isSelected || state.isFocused ? '#F3E7FF' : 'transparent',
    color: '#1E1B4B',
    fontWeight: state.isSelected ? 600 : 400,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base: any) => ({ ...base, padding: 4, color: '#D6B0FF' }),
}

// Collapsed, the rail has no room for the business name, and a generic building
// glyph answers "there is a business" rather than "which one".
function initials(name: string): string {
  return name
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('')
}

export const RailClientSelector = ({ expanded, onRequestExpand }: Props) => {
  const businessID = useGetBusinessIDIfExists()
  const businesses = useGetAllBusinesses()
  const switchBusiness = useSwitchBusiness()

  const options: Option[] = businesses.map(b => ({ value: b.id, label: b.businessName }))
  const value = businessID ? options.find(o => o.value === Number(businessID)) ?? null : null

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={onRequestExpand}
        title={value ? value.label : 'Pick a business'}
        aria-label={value ? value.label : 'Pick a business'}
        className="flex h-[40px] w-[40px] items-center justify-center rounded-lg border border-mm-primary-500 bg-mm-primary-950 transition-colors hover:border-mm-primary-400"
      >
        {value ? (
          <span className="text-[12px] font-semibold tracking-wide text-mm-neutral-1-50">
            {initials(value.label)}
          </span>
        ) : (
          <i className="fas fa-building text-[18px] text-mm-primary-200" aria-hidden />
        )}
      </button>
    )
  }

  return (
    <Select
      options={options}
      value={value}
      onChange={(option: any) => switchBusiness(option.value)}
      styles={railSelectStyles}
      placeholder="Pick a business"
      menuPlacement="auto"
      aria-label="Business"
    />
  )
}
